import React, { useState } from 'react'
import { Alert, Box, Button, Card, CardContent, TextField, Typography } from '@mui/material'
import { useNotify } from 'react-admin'

import { IDwUsageStatus, triggerDwMeasure } from '../../Services/datawarehouse'

interface Props {
	status: IDwUsageStatus
	onDone: () => void
}

// Les deux gestes possibles : la mesure du jour, et la reprise d'historique.
//
// ⚠️ Ils sont SÉPARÉS volontairement, alors que c'est le même appel avec une
// fenêtre différente. Le scan de l'export de facturation est payant et
// proportionnel à la fenêtre : un seul bouton pré-rempli à 400 jours se
// cliquerait par réflexe, et une reprise complète se paie à chaque clic.

// #region DwMeasureActions
export const DwMeasureActions: React.FC<Props> = ({ status, onDone }) => {
	const notify = useNotify()
	const [days, setDays] = useState(String(status.maxWindowDays))
	const [running, setRunning] = useState(false)

	const run = async (n?: number) => {
		setRunning(true)
		try {
			const res = await triggerDwMeasure(n)
			notify(`${res.rows} ligne(s) écrite(s) pour ${res.projects} projet(s)`, { type: 'success' })
			onDone()
		} catch (e: any) {
			// 409 = une mesure tourne déjà (verrou partagé avec le cron). Ce n'est
			// pas une panne, et le dire évite un second clic qui ne servirait à rien.
			notify(e?.message || 'Échec de la mesure', { type: 'error' })
		} finally {
			setRunning(false)
		}
	}

	const n = Number(days)
	const invalid = !Number.isFinite(n) || n < 1 || n > status.maxWindowDays

	return (
		<Card>
			<CardContent>
				<Typography variant="h6" gutterBottom>
					Reprises
				</Typography>

				<Box sx={{ mb: 2 }}>
					<Button variant="outlined" disabled={running} onClick={() => run()}>
						{running ? 'En cours…' : `Mesurer maintenant (${status.measureWindowDays} jours)`}
					</Button>
					<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
						Le passage du cron, à la demande. La fenêtre de {status.measureWindowDays} jours
						couvre les révisions que Google publie après coup.
					</Typography>
				</Box>

				{/* ⚠️ Le coût est dit AVANT le bouton, pas après : c'est la seule
				    information qui doit arrêter la main de quelqu'un qui clique vite. */}
				<Alert severity="warning" sx={{ mb: 1.5 }}>
					Une reprise longue relit l’export de facturation sur toute la fenêtre. Ce scan est
					facturé par BigQuery, proportionnellement au nombre de jours.
				</Alert>

				{/* ⚠️ Le texte d'aide est SOUS la ligne, pas dans le TextField : en
				    helperText, il ajoute deux lignes sous le seul champ et décale le
				    bouton vers le haut. Les deux éléments gardent ainsi la même
				    hauteur et s'alignent d'eux-mêmes. */}
				<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
					<TextField
						label="Jours à reprendre"
						size="small"
						value={days}
						onChange={(e) => setDays(e.target.value)}
						error={invalid}
						sx={{ width: 200 }}
					/>
					<Button variant="contained" disabled={running || invalid} onClick={() => run(n)}>
						{running ? 'En cours…' : 'Lancer la reprise'}
					</Button>
				</Box>
				<Typography
					variant="caption"
					color={invalid ? 'error' : 'text.secondary'}
					sx={{ display: 'block', mt: 0.75 }}
				>
					{invalid
						? `Entre 1 et ${status.maxWindowDays} jours.`
						: `Maximum ${status.maxWindowDays} jours — au-delà, la purge a effacé les journées (rétention ${status.retentionDays} jours).`}
				</Typography>
			</CardContent>
		</Card>
	)
}

// #endregion
